import Globe from "@/inline-assets/globe.svg";

export const DEFAULT_MATCHES_LIMIT = 5;
export { PLACEMENT, TEAM_IDS } from "@/game-csgo/utils-cs2.mjs";

export const GAME_PHASES = {
  WARMUP_ROUND: 0,
  PLAYING_STANDARD: 1,
  PLAYING_FIRST_HALF: 2,
  PLAYING_SECOND_HALF: 3,
  HALFTIME: 4,
  MATCH_ENDED: 5,
  MAX: 6,
};

export const ROUND_END_RESULT = {
  CT_WIN_ELIMINATION: 1,
  CT_WIN_RESCUE: 2,
  CT_WIN_DEFUSE: 3,
  CT_WIN_TIME: 4,
  T_WIN_ELIMINATION: 5,
  T_WIN_BOMB: 6,
  T_WIN_TIME: 7,
};

export const BOT_STEAM_ID = "STEAM_0:0:0";

type WeaponCategory = {
  value: string;
  label: [string, string];
};

export const weaponCategories: WeaponCategory[] = [
  {
    value: "all",
    label: ["common:weaponCategories.all", "All Weapons"],
  },
  {
    value: "smg",
    label: ["common:weaponCategories.smg", "SMGs"],
  },
  {
    value: "shotgun",
    label: ["common:weaponCategories.shotgun", "Shotguns"],
  },
  {
    value: "sidearm",
    label: ["common:weaponCategories.sidearm", "Sidearms"],
  },
  {
    value: "rifle",
    label: ["common:weaponCategories.rifle", "Rifles"],
  },
  {
    value: "heavy",
    label: ["common:weaponCategories.heavy", "Heavy"],
  },
  {
    value: "sniper",
    label: ["common:weaponCategories.sniper", "Snipers"],
  },
  {
    value: "gear",
    label: ["common:weaponCategories.gear", "Gear"],
  },
];

export const GAME_MODE_TYPES = {
  ALL: "all",
  COMPETITIVE: "competitive",
  WINGMAN: "wingman",
  CASUAL: "casual",
  ARMS_RACE: "armsRace",
  DEMOLITION: "demolition",
  DEATHMATCH: "deathmatch",
  RETAKES: "retakes",
  DANGER_ZONE: "dangerZone",
  FLYING_SCOUTMAN: "flyingScoutman",
};

export interface GameMode {
  id: "all" | "casual" | "competitive" | "wingman" | "deathmatch" | "armsRace";
  type: string;
  t: string;
  label: string;
  rankName?: string;
  key?: string;
  showAggregateStats: boolean;
  img?: string;
  altId: string;
  hidden?: boolean;
  scoringMethod?: string;
  rounds?: boolean;
  maxRounds?: number;
}

// gameType_gameMode_maxRounds
export const GAME_MODES: Record<string, GameMode> = {
  ALL: {
    id: "all",
    type: GAME_MODE_TYPES.ALL,
    t: "common:overall",
    label: "Overall",
    rankName: "competitiveRank",
    img: "competitive",
    showAggregateStats: true,
    altId: "ALL",
  },
  "0_0": {
    id: "casual",
    type: GAME_MODE_TYPES.CASUAL,
    t: "csgo:modes.casual",
    label: "Casual",
    key: "0_0_15",
    scoringMethod: "roundsNoHalf",
    rounds: true,
    img: "casual",
    showAggregateStats: true,
    altId: "casual",
    maxRounds: 15,
  },
  "0_1": {
    id: "competitive",
    type: GAME_MODE_TYPES.COMPETITIVE,
    t: "csgo:modes.competitive",
    label: "Competitive",
    key: "0_1_24",
    rankName: "competitiveRank",
    rounds: true,
    showAggregateStats: false,
    altId: "competitive",
    maxRounds: 24,
  },
  "0_2": {
    id: "wingman",
    type: GAME_MODE_TYPES.WINGMAN,
    t: "csgo:modes.wingman",
    label: "Wingman",
    key: "0_2_16",
    rankName: "wingmanRank",
    scoringMethod: "rounds",
    rounds: true,
    img: "wingman",
    showAggregateStats: true,
    altId: "wingman",
    maxRounds: 16,
  },
  // This has been updated for CS2
  // "1_2": {
  //   id: "deathmatch",
  //   type: GAME_MODE_TYPES.DEATHMATCH,
  //   t: "csgo:modes.deathmatch",
  //   label: "Deathmatch",
  //   key: "1_2_0",
  //   img: "deathmatch",
  //   showAggregateStats: false,
  //   altId: "deathmatch",
  // },
  "1_0": {
    id: "armsRace",
    type: GAME_MODE_TYPES.ARMS_RACE,
    t: "csgo:modes.armsRace",
    label: "Arms Race",
    key: "1_0_0",
    img: "armsRace",
    showAggregateStats: true,
    altId: "armsRace",
  },
};

export const gameModes = Object.values(GAME_MODES);

export const DEFAULT_GAME_MODE_KEY = "0_0_15";

export const GAME_MODE_RANKS = {
  COMPETITIVE: 0,
  DANGER_ZONE: 6,
} as const;

type RankInfo = {
  i18nKey: string;
  icon: string;
  name: string;
};

type AllModeRanks = {
  [GAME_MODE_RANKS.COMPETITIVE]: (RankInfo & { smallIcon: string })[];
  [GAME_MODE_RANKS.DANGER_ZONE]: RankInfo[];
};

export const RANKS: AllModeRanks = {
  [GAME_MODE_RANKS.COMPETITIVE]: [
    {
      i18nKey: "unranked",
      icon: "none",
      name: "Unranked",
      smallIcon: "unranked",
    },
    {
      i18nKey: "silver_1",
      icon: "silver_1",
      name: "Silver I",
      smallIcon: "silver_1",
    },
    {
      i18nKey: "silver_2",
      icon: "silver_2",
      name: "Silver II",
      smallIcon: "silver_2",
    },
    {
      i18nKey: "silver_3",
      icon: "silver_3",
      name: "Silver III",
      smallIcon: "silver_3",
    },
    {
      i18nKey: "silver_4",
      icon: "silver_4",
      name: "Silver IV",
      smallIcon: "silver_4",
    },
    {
      i18nKey: "silverElite",
      icon: "silver_elite",
      name: "Silver Elite",
      smallIcon: "silver_elite",
    },
    {
      i18nKey: "silverEliteMaster",
      icon: "silver_elite_master",
      name: "Silver Elite Master",
      smallIcon: "silver_elite_master",
    },
    {
      i18nKey: "goldNova_1",
      icon: "gold_nova_1",
      name: "Gold Nova I",
      smallIcon: "gold_nova_1",
    },
    {
      i18nKey: "goldNova_2",
      icon: "gold_nova_2",
      name: "Gold Nova II",
      smallIcon: "gold_nova_2",
    },
    {
      i18nKey: "goldNova_3",
      icon: "gold_nova_3",
      name: "Gold Nova III",
      smallIcon: "gold_nova_3",
    },
    {
      i18nKey: "goldNovaMaster",
      icon: "gold_nova_master",
      name: "Gold Nova Master",
      smallIcon: "gold_nova_master",
    },
    {
      i18nKey: "masterGuardian_1",
      icon: "master_guardian_1",
      name: "Master Guardian I",
      smallIcon: "master_guardian",
    },
    {
      i18nKey: "masterGuardian_2",
      icon: "master_guardian_2",
      name: "Master Guardian II",
      smallIcon: "master_guardian_2",
    },
    {
      i18nKey: "masterGuardianElite",
      icon: "master_guardian_elite",
      name: "Master Guardian Elite",
      smallIcon: "master_guardian_elite",
    },
    {
      i18nKey: "distinguishedMasterGuardian",
      icon: "distinguished_master_guardian",
      name: "Distinguished Master Guardian",
      smallIcon: "distinguished_master_guardian",
    },
    {
      i18nKey: "legendaryEagle",
      icon: "legendary_eagle",
      name: "Legendary Eagle",
      smallIcon: "legendary_eagle",
    },
    {
      i18nKey: "legendaryEagleMaster",
      icon: "legendary_eagle_master",
      name: "Legendary Eagle Master",
      smallIcon: "legendary_eagle_master",
    },

    {
      i18nKey: "supremeMasterFirstClass",
      icon: "supreme_master_first_class",
      name: "Supreme Master First Class",
      smallIcon: "supreme_master_first_class",
    },
    {
      i18nKey: "globalElite",
      icon: "global_elite",
      name: "Global Elite",
      smallIcon: "global_elite",
    },
    {
      i18nKey: "expired",
      icon: "expired",
      name: "Expired",
      smallIcon: "expired",
    },
  ],
  [GAME_MODE_RANKS.DANGER_ZONE]: [
    { i18nKey: "unranked", icon: "none", name: "Unranked" },
    {
      i18nKey: "labRat_1",
      icon: "dg_lab_rat_1",
      name: "Lab Rat I",
    },
    {
      i18nKey: "labRat_2",
      icon: "dg_lab_rat_2",
      name: "Lab Rat II",
    },
    {
      i18nKey: "sprintingHare_1",
      icon: "dg_sprinting_hare_1",
      name: "Sprinting Hare I",
    },
    {
      i18nKey: "sprintingHare_2",
      icon: "dg_sprinting_hare_2",
      name: "Sprinting Hare II",
    },
    {
      i18nKey: "wildScout_1",
      icon: "dg_wild_scout_1",
      name: "Wild Scout I",
    },
    {
      i18nKey: "wildScout_2",
      icon: "dg_wild_scout_2",
      name: "Wild Scout II",
    },
    {
      i18nKey: "wildScoutElite",
      icon: "dg_wild_scout_elite",
      name: "Wild Scout Elite",
    },
    {
      i18nKey: "hunterFox_1",
      icon: "dg_hunter_fox_1",
      name: "Hunter Fox I",
    },
    {
      i18nKey: "hunterFox_2",
      icon: "dg_hunter_fox_2",
      name: "Hunter Fox II",
    },

    {
      i18nKey: "hunterFox_3",
      icon: "dg_hunter_fox_3",
      name: "Hunter Fox III",
    },
    {
      i18nKey: "hunterFoxElite",
      icon: "dg_hunter_fox_elite",
      name: "Hunter Fox Elite",
    },
    {
      i18nKey: "timberWolf",
      icon: "dg_timber_wolf",
      name: "Timber Wolf",
    },
    {
      i18nKey: "emberWolf",
      icon: "dg_ember_wolf",
      name: "Ember Wolf",
    },
    {
      i18nKey: "wildfireWolf",
      icon: "dg_wildfire_wolf",
      name: "Wildfire Wolf",
    },
    {
      i18nKey: "theHowlingAlpha",
      icon: "dg_the_howling_alpha",
      name: "The Howling Alpha",
    },
  ],
};

interface Region {
  key: string;
  steam_id: number;
  text: Translation;
  short: string;
  icon?: SVGComponent;
}

export const REGIONS: Region[] = [
  {
    key: "global",
    steam_id: 0,
    text: ["common:regions.global", "Global"],
    short: "GBL",
    icon: Globe,
  },
  {
    key: "africa",
    steam_id: 7,
    text: ["common:regions.africa", "Africa"],
    short: "AF",
  },
  {
    key: "asia",
    steam_id: 4,
    text: ["common:regions.asia", "Asia"],
    short: "AS",
  },
  {
    key: "australia",
    steam_id: 5,
    text: ["common:regions.australia", "Australia"],
    short: "AU",
  },
  {
    key: "china",
    steam_id: 9,
    text: ["common:regions.china", "China"],
    short: "CN",
  },
  {
    key: "europe",
    steam_id: 3,
    text: ["common:regions.europe", "Europe"],
    short: "EU",
  },
  {
    key: "north-america",
    steam_id: 1,
    text: ["common:regions.northAmerica", "North America"],
    short: "NA",
  },
  {
    key: "south-america",
    steam_id: 2,
    text: ["common:regions.southAmerica", "South America"],
    short: "SA",
  },
];

export const dangerZoneRanks = [
  "ember_wolf",
  "lab_rat_1",
  "timber_wolf",
  "hunter_fox_1",
  "lab_rat_2",
  "wild_scout_1",
  "hunter_fox_2",
  "sprinting_hare_1",
  "wild_scout_2",
  "hunter_fox_3",
  "sprinting_hare_2",
  "wild_scout_elite",
  "hunter_fox_elite",
  "the_howling_alpha",
  "wildfire_wolf",
];

export const MAPS = {
  ar_baggage: {
    i18nKey: "arBaggage",
    name: "Baggage",
  },
  // ar_dizzy: {
  //   i18nKey: "arDizzy",
  //   name: "Dizzy",
  // },
  // ar_lunacy: {
  //   i18nKey: "arLunacy",
  //   name: "Lunacy",
  // },
  // ar_monastery: {
  //   i18nKey: "arMonastery",
  //   name: "Monastery",
  // },
  ar_pool_day: {
    i18nKey: "arPoolDay",
    name: "Pool Day",
  },
  ar_shoots: {
    i18nKey: "arShoots",
    name: "Shoots",
  },
  // cp_dust2: {
  //   i18nKey: "cpDust2",
  //   name: "Dust II Control",
  // },
  // cp_rooftop: {
  //   i18nKey: "cpRooftop",
  //   name: "Rooftop Control",
  // },
  // cs_agency: {
  //   i18nKey: "csAgency",
  //   name: "Agency",
  // },
  // cs_assault: {
  //   i18nKey: "csAssault",
  //   name: "Assault",
  // },
  // cs_climb: {
  //   i18nKey: "csClimb",
  //   name: "Climb",
  // },
  cs_italy: {
    i18nKey: "csItaly",
    name: "Italy",
  },
  // cs_militia: {
  //   i18nKey: "csMilitia",
  //   name: "Militia",
  // },
  cs_office: {
    i18nKey: "csOffice",
    name: "Office",
  },
  de_ancient: {
    i18nKey: "deAncient",
    name: "Ancient",
  },
  de_anubis: {
    i18nKey: "deAnubis",
    name: "Anubis",
  },
  de_assembly: {
    i18nKey: "deAssembly",
    name: "Assembly",
  },
  // de_bank: {
  //   i18nKey: "deBank",
  //   name: "Bank",
  // },
  de_basalt: {
    i18nKey: "deBasalt",
    name: "Basalt",
  },
  // de_blagai: {
  //   i18nKey: "deBlagai",
  //   name: "Blagai",
  // },
  // de_breach: {
  //   i18nKey: "deBreach",
  //   name: "Breach",
  // },
  // de_cache: {
  //   i18nKey: "deCache",
  //   name: "Cache",
  // },
  de_canals: {
    i18nKey: "deCanals",
    name: "Canals",
  },
  // de_cbble: {
  //   i18nKey: "deCbble",
  //   name: "Cobblestone",
  // },
  // de_chalice: {
  //   i18nKey: "deChalice",
  //   name: "Chalice",
  // },
  // de_crete: {
  //   i18nKey: "deCrete",
  //   name: "Crete",
  // },
  de_dust2: {
    i18nKey: "deDust2",
    name: "Dust II",
  },
  de_edin: {
    i18nKey: "deEdin",
    name: "Edin",
  },
  // de_hive: {
  //   i18nKey: "deHive",
  //   name: "Hive",
  // },
  de_inferno: {
    i18nKey: "deInferno",
    name: "Inferno",
  },
  // de_iris: {
  //   i18nKey: "deIris",
  //   name: "Iris",
  // },
  de_lake: {
    i18nKey: "deLake",
    name: "Lake",
  },
  de_memento: {
    i18nKey: "deMemento",
    name: "Memento",
  },
  de_mills: {
    i18nKey: "deMills",
    name: "Mills",
  },
  de_mirage: {
    i18nKey: "deMirage",
    name: "Mirage",
  },
  de_nuke: {
    i18nKey: "deNuke",
    name: "Nuke",
  },
  de_overpass: {
    i18nKey: "deOverpass",
    name: "Overpass",
  },
  de_palais: {
    i18nKey: "dePalais",
    name: "Palais",
  },
  // de_prime: {
  //   i18nKey: "deCascade",
  //   name: "Cascade",
  // },
  // de_safehouse: {
  //   i18nKey: "deSafehouse",
  //   name: "Safehouse",
  // },
  de_shortdust: {
    i18nKey: "deShortdust",
    name: "Shortdust",
  },
  // de_shortnuke: {
  //   i18nKey: "deShortnuke",
  //   name: "Shortnuke",
  // },
  // de_stmarc: {
  //   i18nKey: "deStmarc",
  //   name: "St. Marc",
  // },
  // de_sugarcane: {
  //   i18nKey: "deSugarcane",
  //   name: "Sugarcane",
  // },
  de_thera: {
    i18nKey: "deThera",
    name: "Thera",
  },
  de_train: {
    i18nKey: "deTrain",
    name: "Train",
  },
  // de_tuscan: {
  //   i18nKey: "deTuscan",
  //   name: "Tuscan",
  // },
  de_vertigo: {
    i18nKey: "deVertigo",
    name: "Vertigo",
  },
  de_whistle: {
    i18nKey: "deWhistle",
    name: "Whistle",
  },
  // dz_blacksite: {
  //   i18nKey: "dzBlacksite",
  //   name: "Blacksite",
  // },
  // dz_ember: {
  //   i18nKey: "dzEmber",
  //   name: "Ember",
  // },
  // dz_sirocco: {
  //   i18nKey: "dzSirocco",
  //   name: "Sirocco",
  // },
  // dz_vineyard: {
  //   i18nKey: "dzVineyard",
  //   name: "Vineyard",
  // },
};

export const FILTER_MAPS = {
  ALL: {
    i18nKey: "allMaps",
    name: "All Maps",
  },
  ...MAPS,
};

interface Weapon {
  key: string;
  i18nKey: string;
  icon: string;
  name: string;
  category: string;
}

// https://tf2b.com/itemlist.php?gid=730
export const WEAPONS: Record<number, Weapon> = {
  26: {
    key: "weapon_bizon",
    i18nKey: "weaponBizon",
    icon: "bizon",
    name: "PP-Bizon",
    category: "smg",
  },
  1: {
    key: "weapon_deagle",
    i18nKey: "weaponDeagle",
    icon: "deagle",
    name: "Desert Eagle",
    category: "sidearm",
  },
  63: {
    key: "weapon_cz75a",
    i18nKey: "weaponCz75a",
    icon: "cz75a",
    name: "CZ75-Auto",
    category: "sidearm",
  },
  2: {
    key: "weapon_elite",
    i18nKey: "weaponElite",
    icon: "elite",
    name: "Dual Berettas",
    category: "sidearm",
  },
  10: {
    key: "weapon_famas",
    i18nKey: "weaponFamas",
    icon: "famas",
    name: "FAMAS",
    category: "rifle",
  },
  3: {
    key: "weapon_fiveseven",
    i18nKey: "weaponFiveseven",
    icon: "fiveseven",
    name: "Five-SeveN",
    category: "sidearm",
  },
  13: {
    key: "weapon_galilar",
    i18nKey: "weaponGalilar",
    icon: "galilar",
    name: "Galil AR",
    category: "rifle",
  },
  4: {
    key: "weapon_glock",
    i18nKey: "weaponGlock",
    icon: "glock",
    name: "Glock-18",
    category: "sidearm",
  },
  32: {
    key: "weapon_hkp2000",
    i18nKey: "weaponP2000",
    icon: "hkp2000",
    name: "P2000",
    category: "sidearm",
  },
  42: {
    key: "weapon_knife",
    i18nKey: "weaponKnife",
    icon: "knife",
    name: "Knife",
    category: "gear",
  },
  14: {
    key: "weapon_m249",
    i18nKey: "weaponM249",
    icon: "m249",
    name: "M249",
    category: "heavy",
  },
  60: {
    key: "weapon_m4a1_silencer",
    i18nKey: "weaponM4a1Silencer",
    icon: "m4a1_silencer",
    name: "M4A1-S",
    category: "rifle",
  },
  16: {
    key: "weapon_m4a1",
    i18nKey: "weaponM4A1",
    icon: "m4a1",
    name: "M4A1",
    category: "rifle",
  },
  27: {
    key: "weapon_mag7",
    i18nKey: "weaponMag7",
    icon: "mag7",
    name: "MAG-7",
    category: "shotgun",
  },
  18: {
    key: "weapon_mp5sd",
    i18nKey: "weaponMp5sd",
    icon: "mp5sd",
    name: "MP5-SD",
    category: "smg",
  },
  33: {
    key: "weapon_mp7",
    i18nKey: "weaponMp7",
    icon: "mp7",
    name: "MP7",
    category: "smg",
  },
  34: {
    key: "weapon_mp9",
    i18nKey: "weaponMp9",
    icon: "mp9",
    name: "MP9",
    category: "smg",
  },
  28: {
    key: "weapon_negev",
    i18nKey: "weaponNegev",
    icon: "negev",
    name: "Negev",
    category: "heavy",
  },
  35: {
    key: "weapon_nova",
    i18nKey: "weaponNova",
    icon: "nova",
    name: "Nova",
    category: "shotgun",
  },
  36: {
    key: "weapon_p250",
    i18nKey: "weaponP250",
    icon: "p250",
    name: "P250",
    category: "sidearm",
  },
  64: {
    key: "weapon_revolver",
    i18nKey: "weaponRevolver",
    icon: "revolver",
    name: "R8 Revolver",
    category: "sidearm",
  },
  29: {
    key: "weapon_sawedoff",
    i18nKey: "weaponSawedoff",
    icon: "sawedoff",
    name: "Sawed-Off",
    category: "shotgun",
  },
  38: {
    key: "weapon_scar20",
    i18nKey: "weaponScar20",
    icon: "scar20",
    name: "SCAR-20",
    category: "sniper",
  },
  39: {
    key: "weapon_sg556",
    i18nKey: "weaponSg556",
    icon: "sg556",
    name: "SG 553",
    category: "rifle",
  },
  40: {
    key: "weapon_ssg08",
    i18nKey: "weaponSsg08",
    icon: "ssg08",
    name: "SSG 08",
    category: "sniper",
  },
  30: {
    key: "weapon_tec9",
    i18nKey: "weaponTec9",
    icon: "tec9",
    name: "Tec-9",
    category: "sidearm",
  },
  61: {
    key: "weapon_usp_silencer",
    i18nKey: "weaponUspSilencer",
    icon: "usp_silencer",
    name: "USP-S",
    category: "sidearm",
  },
  25: {
    key: "weapon_xm1014",
    i18nKey: "weaponXm1014",
    icon: "xm1014",
    name: "XM1014",
    category: "shotgun",
  },
  17: {
    key: "weapon_mac_10",
    i18nKey: "weaponMac_10",
    icon: "mac_10",
    name: "MAC-10",
    category: "smg",
  },
  19: {
    key: "weapon_p90",
    i18nKey: "weaponP90",
    icon: "p90",
    name: "P90",
    category: "smg",
  },
  24: {
    key: "weapon_ump45",
    i18nKey: "weaponUmp45",
    icon: "ump45",
    name: "UMP-45",
    category: "smg",
  },
  7: {
    key: "weapon_ak47",
    i18nKey: "weaponAk47",
    icon: "ak47",
    name: "AK-47",
    category: "rifle",
  },
  8: {
    key: "weapon_aug",
    i18nKey: "weaponAug",
    icon: "aug",
    name: "AUG",
    category: "rifle",
  },
  9: {
    key: "weapon_awp",
    i18nKey: "weaponAwp",
    icon: "awp",
    name: "AWP",
    category: "sniper",
  },
  11: {
    key: "weapon_g3sg1",
    i18nKey: "weaponG3sg1",
    icon: "g3sg1",
    name: "G3SG1",
    category: "sniper",
  },
  31: {
    key: "weapon_taser",
    i18nKey: "weaponTaser",
    icon: "taser",
    name: "Zeus x27",
    category: "gear",
  },
  // weapon_smokegrenade: {
  //   key: "weapon_smokegrenade",
  //   i18nKey: "weaponSmokegrenade",
  //   icon: WeaponSmokegrenade,
  //   name: "Smoke Grenade",
  //   category: "gear",
  // },
  46: {
    key: "weapon_incgrenade",
    i18nKey: "weaponIncgrenade",
    icon: "incgrenade",
    name: "Incendiary Grenade",
    category: "gear",
  },
  44: {
    key: "weapon_hegrenade",
    i18nKey: "weaponHegrenade",
    icon: "hegrenade",
    name: "HE Grenade",
    category: "gear",
  },
  // weapon_flashbang: {
  //   i18nKey: "weaponFlashbang",
  //   icon: WeaponFlashbang,
  //   name: "Flashbang",
  //   category: "gear",
  // },
  47: {
    key: "weapon_decoy",
    i18nKey: "weaponDecoy",
    icon: "decoy",
    name: "Decoy Grenade",
    category: "gear",
  },
};

// 35: 61, //silence pistol
// 8: 60, //m4a1 silence
// 39: 63, // cz75a
// 0: 64, // revolver
